import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import AdminHeader from '../components/AdminHeader';
import ConfirmModal from '../components/ConfirmModal';
import { escapeCSV, downloadCSV, formatDate } from '../utils/csvExport';
import '../styles/AdminPromotions.css';
import './AdminRewardsPage_Mobile.css';

function AdminRewardsPage() {
  const [rewards, setRewards] = useState([]);
  const [form, setForm] = useState({
    name: '',
    description: '',
    type: 'cart',
    valueType: 'percent',
    value: 0,
    minCartAmount: 0,
    expirationDate: '',
    active: true,
    pointCost: 0,
  });
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRewardId, setDeleteRewardId] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const token = localStorage.getItem('token');

  const fetchRewards = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API}/api/rewards`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRewards(res.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load rewards');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchRewards(); }, [fetchRewards]);

  const openModal = (reward = null) => {
    if (reward) {
      setForm({ ...reward, expirationDate: reward.expirationDate ? reward.expirationDate.slice(0,10) : '' });
      setEditingId(reward._id);
    } else {
      setForm({ name: '', description: '', type: 'cart', valueType: 'percent', value: 0, minCartAmount: 0, expirationDate: '', active: true, pointCost: 0 });
      setEditingId(null);
    }
    setShowModal(true);
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${process.env.REACT_APP_API}/api/rewards/${editingId}`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${process.env.REACT_APP_API}/api/rewards`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setShowModal(false);
      setForm({ name: '', description: '', type: 'cart', valueType: 'percent', value: 0, minCartAmount: 0, expirationDate: '', active: true, pointCost: 0 });
      setEditingId(null);
      await fetchRewards();
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving reward');
    }
  };

  const handleEdit = (r) => openModal(r);

  const handleDelete = async id => {
    setDeleteRewardId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteRewardId) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API}/api/rewards/${deleteRewardId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchRewards();
    } catch (err) {
      console.error('Failed to delete reward:', err);
    }
    setDeleteRewardId(null);
  };

  const handleActivate = async (id) => {
    try {
      await axios.put(`${process.env.REACT_APP_API}/api/rewards/${id}`, { active: true }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchRewards();
    } catch (err) {
      alert('Failed to activate reward');
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await axios.put(`${process.env.REACT_APP_API}/api/rewards/${id}`, { active: false }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchRewards();
    } catch (err) {
      alert('Failed to deactivate reward');
    }
  };

  const handleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleBulkActivate = async () => {
    if (selected.length === 0) return;
    try {
      await Promise.all(selected.map(id => axios.put(`${process.env.REACT_APP_API}/api/rewards/${id}`, { active: true }, { headers: { Authorization: `Bearer ${token}` } } )));
      setSelected([]);
      await fetchRewards();
    } catch (err) {
      alert('Failed to activate selected rewards');
    }
  };

  const handleBulkDeactivate = async () => {
    if (selected.length === 0) return;
    try {
      await Promise.all(selected.map(id => axios.put(`${process.env.REACT_APP_API}/api/rewards/${id}`, { active: false }, { headers: { Authorization: `Bearer ${token}` } } )));
      setSelected([]);
      await fetchRewards();
    } catch (err) {
      alert('Failed to deactivate selected rewards');
    }
  };

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    try {
      await Promise.all(selected.map(id => axios.delete(`${process.env.REACT_APP_API}/api/rewards/${id}`, { headers: { Authorization: `Bearer ${token}` } })));
      setSelected([]);
      await fetchRewards();
    } catch (err) {
      console.error('Failed to delete selected rewards:', err);
    }
  };

  const exportToCSV = () => {
    let csvContent = 'Name,Description,Type,Value Type,Value,Min Cart Amount,Point Cost,Expiration Date,Active\n';
    
    rewards.forEach(reward => {
      const name = escapeCSV(reward.name || '');
      const description = escapeCSV(reward.description || '');
      const type = escapeCSV(reward.type || '');
      const valueType = escapeCSV(reward.valueType || '');
      const value = escapeCSV(reward.value || 0);
      const minCart = escapeCSV(reward.minCartAmount || 0);
      const pointCost = escapeCSV(reward.pointCost || 0);
      const expiration = escapeCSV(reward.expirationDate ? formatDate(reward.expirationDate) : 'N/A');
      const active = escapeCSV(reward.active ? 'Yes' : 'No');
      
      csvContent += `${name},${description},${type},${valueType},${value},${minCart},${pointCost},${expiration},${active}\n`;
    });
    
    const filename = `rewards_backup_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  if (loading) {
    return (
      <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Green Header Bar */}
        <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }} />
        
        {/* Navbar */}
        <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
          <div style={{ width: '100%', margin: '0 auto', background: 'transparent' }}>
            <AdminHeader />
          </div>
        </div>

        {/* Loading State */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1, rotate: 360 }}
            transition={{ duration: 0.6, rotate: { duration: 1, repeat: Infinity, ease: 'linear' } }}
            style={{ fontSize: '4rem' }}
          >
            🌱
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }} />
      <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
        <div style={{ width: '100%', margin: '0 auto', background: 'transparent' }}>
          <AdminHeader />
        </div>
      </div>
      <div style={{ width: '100%', margin: '40px auto', padding: '0 20px', flex: 1, boxSizing: 'border-box' }}>
        <div className="admin-promotions admin-rewards-page">
          <motion.div
            className="rewards-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h1>Manage Rewards Program</h1>
            <div style={{ display: 'flex', gap: 12 }}>
              <motion.button 
                onClick={exportToCSV}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  background: '#2e7d32',
                  color: '#fff',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(46,125,50,0.2)'
                }}
              >
                📊 Export CSV
              </motion.button>
              <motion.button 
                className="add-reward-btn"
                onClick={() => openModal()}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                + Add Reward
              </motion.button>
            </div>
          </motion.div>

          <AnimatePresence mode="wait">
            {error && (
              <motion.div 
                className="error-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {selected.length > 0 && (
              <motion.div 
                className="bulk-actions"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <span className="bulk-count">{selected.length} selected</span>
                <div className="bulk-buttons">
                  <motion.button 
                    onClick={handleBulkActivate}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-activate"
                  >
                    ✓ Activate
                  </motion.button>
                  <motion.button 
                    onClick={handleBulkDeactivate}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-deactivate"
                  >
                    ✕ Deactivate
                  </motion.button>
                  <motion.button 
                    onClick={handleBulkDelete}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-delete"
                  >
                    🗑 Delete
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Desktop Table View */}
          {rewards.length === 0 ? (
            <motion.div 
              className="empty-state"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="empty-state-icon">🎁</div>
              <h3 className="empty-state-title">No Rewards Yet</h3>
              <p className="empty-state-text">Create your first reward to start offering points-based discounts to customers</p>
            </motion.div>
          ) : (
            <>
              <motion.div 
                className="rewards-table-container desktop-only"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <table className="rewards-table">
                  <thead>
                    <tr>
                      <th><input type="checkbox" onChange={(e) => setSelected(e.target.checked ? rewards.map(r => r._id) : [])} checked={selected.length === rewards.length && rewards.length > 0} /></th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Discount</th>
                      <th>Point Cost</th>
                      <th>Min Cart</th>
                      <th>Expires</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {rewards.map((r, idx) => {
                    const isExpired = r.expirationDate && new Date(r.expirationDate) < new Date();
                    return (
                      <motion.tr 
                        key={r._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.2, delay: idx * 0.03 }}
                        className={isExpired ? 'expired-row' : ''}
                      >
                        <td>
                          <input type="checkbox" checked={selected.includes(r._id)} onChange={() => handleSelect(r._id)} />
                        </td>
                        <td><strong>{r.name}</strong></td>
                        <td>
                          <span className={`type-badge ${r.type}`}>
                            {r.type === 'cart' ? '🛒 Cart' : '🚚 Delivery'}
                          </span>
                        </td>
                        <td className="discount-value">{r.valueType === 'percent' ? `${r.value}%` : `₱${r.value}`}</td>
                        <td>{r.pointCost || 0} pts</td>
                        <td>{r.minCartAmount ? `₱${r.minCartAmount}` : '₱0'}</td>
                        <td>
                          <div className="expiry-cell">
                            {r.expirationDate ? new Date(r.expirationDate).toLocaleDateString() : '-'}
                            {isExpired && <span className="expired-badge">⚠ EXPIRED</span>}
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge ${isExpired ? 'expired' : (r.active ? 'active' : 'inactive')}`}>
                            {isExpired ? '⏱ Expired' : (r.active ? '✓ Active' : '✕ Inactive')}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <motion.button 
                              onClick={() => handleEdit(r)}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              className="btn-edit"
                              title="Edit"
                            >
                              <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                            </motion.button>
                            {r.active ? (
                              <motion.button 
                                onClick={() => handleDeactivate(r._id)}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn-toggle active"
                                title="Deactivate"
                              >
                                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                              </motion.button>
                            ) : (
                              <motion.button 
                                onClick={() => handleActivate(r._id)}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn-toggle"
                                title="Activate"
                              >
                                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                              </motion.button>
                            )}
                            <motion.button 
                              onClick={() => handleDelete(r._id)}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              className="btn-delete"
                              title="Delete"
                            >
                              <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M3 6h18v2H3V6zm2 3h14v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9zm3 2v9h2v-9H8zm4 0v9h2v-9h-2z"/></svg>
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </motion.div>

          {/* Mobile Card View */}
          <motion.div 
            className="rewards-grid mobile-only"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <AnimatePresence>
              {rewards.map((r, idx) => {
                const isExpired = r.expirationDate && new Date(r.expirationDate) < new Date();
                return (
                  <motion.div
                    key={r._id}
                    className={`reward-card ${isExpired ? 'expired' : ''}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2, delay: idx * 0.03 }}
                    layout
                  >
                    <div className="card-header">
                      <div className="card-select">
                        <input 
                          type="checkbox" 
                          checked={selected.includes(r._id)} 
                          onChange={() => handleSelect(r._id)}
                          className="checkbox-large"
                        />
                      </div>
                      <div className="card-title-section">
                        <h3>{r.name}</h3>
                        <span className={`type-badge ${r.type}`}>
                          {r.type === 'cart' ? '🛒 Cart' : '🚚 Delivery'}
                        </span>
                      </div>
                      <span className={`status-badge ${isExpired ? 'expired' : (r.active ? 'active' : 'inactive')}`}>
                        {isExpired ? '⏱' : (r.active ? '✓' : '✕')}
                      </span>
                    </div>

                    {r.description && (
                      <p className="card-description">{r.description}</p>
                    )}

                    <div className="card-details">
                      <div className="detail-item">
                        <span className="detail-label">Discount</span>
                        <span className="detail-value discount">
                          {r.valueType === 'percent' ? `${r.value}%` : `₱${r.value}`}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Point Cost</span>
                        <span className="detail-value">{r.pointCost || 0} pts</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Min Cart</span>
                        <span className="detail-value">{r.minCartAmount ? `₱${r.minCartAmount}` : '₱0'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Expires</span>
                        <span className="detail-value">
                          {r.expirationDate ? new Date(r.expirationDate).toLocaleDateString() : 'No expiry'}
                        </span>
                      </div>
                    </div>

                    {isExpired && (
                      <div className="expired-warning">
                        ⚠ This reward has expired
                      </div>
                    )}

                    <div className="card-actions">
                      <motion.button
                        onClick={() => handleEdit(r)}
                        className="btn-card-edit"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                        Edit Reward
                      </motion.button>
                      {r.active ? (
                        <motion.button
                          onClick={() => handleDeactivate(r._id)}
                          className="btn-card-toggle active"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                          Deactivate
                        </motion.button>
                      ) : (
                        <motion.button
                          onClick={() => handleActivate(r._id)}
                          className="btn-card-toggle"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                          Activate
                        </motion.button>
                      )}
                      <motion.button
                        onClick={() => handleDelete(r._id)}
                        className="btn-card-delete"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M3 6h18v2H3V6zm2 3h14v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9zm3 2v9h2v-9H8zm4 0v9h2v-9h-2z"/></svg>
                        Delete
                      </motion.button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
          </>
          )}

          <AnimatePresence>
            {showModal && (
              <motion.div 
                className="modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => setShowModal(false)}
              >
                <motion.div 
                  className="promo-modal"
                  initial={{ scale: 0.9, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.9, y: 20 }}
                  transition={{ duration: 0.2 }}
                  onClick={e => e.stopPropagation()}
                >
                  <div className="modal-header">
                    <h2>{editingId ? '✏️ Edit Reward' : '✨ Add Reward'}</h2>
                    <button 
                      type="button" 
                      className="modal-close"
                      onClick={() => setShowModal(false)}
                    >
                      ×
                    </button>
                  </div>

                  <form className="promo-form" onSubmit={handleSave}>
                    <div className="form-group">
                      <label className="form-label required">Reward Name</label>
                      <input 
                        className="form-input"
                        name="name" 
                        value={form.name} 
                        onChange={handleChange} 
                        required 
                        placeholder="e.g., 100 Points Reward"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Description</label>
                      <textarea 
                        className="form-input"
                        name="description" 
                        value={form.description} 
                        onChange={handleChange}
                        placeholder="Describe the reward benefits..."
                        rows="3"
                        style={{ resize: 'vertical' }}
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Type</label>
                        <select 
                          className="form-select"
                          name="type" 
                          value={form.type} 
                          onChange={handleChange}
                        >
                          <option value="cart">🛒 Cart Discount</option>
                          <option value="delivery">🚚 Delivery Discount</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Value Type</label>
                        <select 
                          className="form-select"
                          name="valueType" 
                          value={form.valueType} 
                          onChange={handleChange}
                        >
                          <option value="percent">📊 Percent (%)</option>
                          <option value="flat">💰 Flat Amount (₱)</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label required">Discount Value</label>
                        <input 
                          className="form-input"
                          name="value" 
                          type="number" 
                          min={0} 
                          value={form.value} 
                          onChange={handleChange} 
                          required
                          placeholder={form.valueType === 'percent' ? '0-100' : '0'}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Point Cost</label>
                        <input 
                          className="form-input"
                          name="pointCost" 
                          type="number" 
                          min={0} 
                          value={form.pointCost} 
                          onChange={handleChange}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Min Cart Amount</label>
                        <input 
                          className="form-input"
                          name="minCartAmount" 
                          type="number" 
                          min={0} 
                          value={form.minCartAmount} 
                          onChange={handleChange}
                          placeholder="0"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Expiration Date</label>
                        <input 
                          className="form-input"
                          name="expirationDate" 
                          type="date" 
                          value={form.expirationDate} 
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <div className="checkbox-group">
                        <label className="checkbox-label">
                          <input 
                            name="active" 
                            type="checkbox" 
                            checked={form.active} 
                            onChange={handleChange}
                          />
                          <span>Active (visible to customers)</span>
                        </label>
                      </div>
                    </div>

                    <div className="modal-actions">
                      <motion.button 
                        type="submit"
                        className="btn-save"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                        Save Reward
                      </motion.button>
                      <motion.button 
                        type="button" 
                        className="btn-cancel"
                        onClick={() => setShowModal(false)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Cancel
                      </motion.button>
                    </div>
                  </form>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </div>
      <div style={{ width: '100%', background: '#175c36', minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 32 }}>
        <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 500, letterSpacing: 1 }}>© {new Date().getFullYear()} Chiqui's Garden</span>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteRewardId(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Reward"
        message="Are you sure you want to delete this reward? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      />

      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        onConfirm={confirmBulkDelete}
        title="Delete Multiple Rewards"
        message={`Are you sure you want to delete ${selected.length} reward(s)? This action cannot be undone.`}
        confirmText="Delete All"
        cancelText="Cancel"
        type="error"
      />
    </div>
  );
}

export default AdminRewardsPage;
